import { Controller } from "@hotwired/stimulus";
import videojs from "video.js";

const FEED_MUTED_STORAGE_KEY = "feedMuted";

function storedFeedMuted() {
  try {
    const value = globalThis.localStorage.getItem(FEED_MUTED_STORAGE_KEY);
    return value === null ? true : value === "true";
  } catch {
    return true;
  }
}

globalThis.feedMuted ??= storedFeedMuted();

// Tracks the single VideoJsController currently allowed to play,
// across the whole feed.

// Keeps a pristine copy of each <video> element, keyed by the stable
// id rendered on its wrapper.
const pristineVideoCache = new Map();

export default class VideoJsController extends Controller {
  static targets = ["video"];

  connect() {
    this.videoElement = this.ensureVideoElement();

    if (!this.videoElement) return;

    this.loadPoster();
    this.sourceUrl = this.videoElement.currentSrc || this.videoElement.src;
    this.networkRetryCount = 0;

    // ----------------------------------------------------
    // Manual Pause State
    // ----------------------------------------------------
    //
    // true  = user explicitly paused this video
    // false = video can be autoplayed when it becomes visible
    //
    // This state is deliberately separate from player.paused().
    // A video can be paused because it left the viewport without
    // being manually paused by the user.
    this.userPaused =
      this.element.dataset.userPaused === "true";

    // Used to distinguish a user clicking the native Video.js
    // play/pause button from an automatic pause().
    this.manualPauseRequested = false;

    // Every play()/pause() call is chained onto this promise so
    // they run strictly one after another for this player.
    this.operationQueue = Promise.resolve();

    this.player = videojs(this.videoElement, {
      controls: true,

      controlBar: {
        children: [
          "playToggle",
          "progressControl",
          "volumePanel",
          "fullscreenToggle"
        ]
      },

      autoplay: false,
      preload: "none",
      loop: false,
      playsinline: true,
      muted: globalThis.feedMuted
    });

    this.setupFullscreenPortal();
    this.addCenterPlayButton();

    // Prevent video controls, including fullscreen, from triggering the clickable post container.
    this.player.el().addEventListener("click", event => {
      event.stopPropagation();
    });
    this.player.el().addEventListener("dblclick", event => {
      event.stopPropagation();
    });

    this.player.ready(() => {
      this.element.videoController = this;

      this.player.muted(globalThis.feedMuted);

      // Tell the feed controller this player can now be played,
      // in case it became visible before Video.js finished initializing.
      this.element
        .closest("[data-controller~='feed-video']")
        ?.feedVideoController?.handleVideoReady();

      this.setupNativePlayToggleTracking();
    });

    // Global mute sync.
    this.player.on("volumechange", this.volumeChanged);
    this.player.on("error", this.handlePlaybackError);

    // ----------------------------------------------------
    // Feed-wide single-video behavior
    // ----------------------------------------------------
    this.player.on("play", () => {
      if (
        VideoJsController.currentlyPlayingController &&
        VideoJsController.currentlyPlayingController !== this
      ) {
        // IMPORTANT:
        // This is NOT a user pause.
        // The previous video is being paused because another
        // video started playing.
        VideoJsController.currentlyPlayingController.pause();
      }

      VideoJsController.currentlyPlayingController = this;
    });

    this.player.on("pause", () => {
      if (VideoJsController.currentlyPlayingController === this) {
        VideoJsController.currentlyPlayingController = null;
      }

      // Only a pause explicitly initiated by the user should
      // set userPaused.
      if (this.manualPauseRequested) {
        this.userPaused = true;
        this.element.dataset.userPaused = "true";

        this.manualPauseRequested = false;
      }
    });

    document.addEventListener(
      "feed:mute-change",
      this.muteListener
    );

    // Turbo freezes a snapshot of the current page via turbo:before-cache.
    this.handleTurboBeforeCache =
      this.handleTurboBeforeCache.bind(this);

    document.addEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCache
    );
  }

  disconnect() {
    document.removeEventListener(
      "feed:mute-change",
      this.muteListener
    );

    document.removeEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCache
    );

    this.removeNativePlayToggleTracking();

    if (VideoJsController.currentlyPlayingController === this) {
      VideoJsController.currentlyPlayingController = null;
    }

    this.destroyPlayer();
  }

  loadPoster() {
    const posterUrl = this.videoElement?.poster;

    if (!posterUrl) {
      this.removeSkeleton();
      return;
    }

    const poster = new Image();

    poster.onload = () => {
      this.removeSkeleton();
    };

    poster.onerror = () => {
      this.removeSkeleton();
    };

    poster.src = posterUrl;

    // Poster may already be loaded/cached.
    if (poster.complete) {
      this.removeSkeleton();
    }
  }

  removeSkeleton() {
    this.element.classList.remove("profile-image-skeleton-dark");
  }

  handleTurboBeforeCache() {
    this.destroyPlayer();
    this.restorePristineVideoElement();
  }

  destroyPlayer() {
    clearTimeout(this.networkRetryTimeout);

    this.element.videoController = null;

    this.restoreFullscreenPlayer?.();

    if (this.player && !this.player.isDisposed()) {
      this.player.dispose();
    }

    this.player = null;
    this.videoElement = null;
  }

  // Video.js removes the native element when disposed.
  // Restore clean markup synchronously so Turbo stores a player
  // that can be initialized again.
  restorePristineVideoElement() {
    const storedClone = pristineVideoCache.get(
      this.cacheKey()
    );

    if (!storedClone) return;

    this.element.replaceChildren(
      storedClone.cloneNode(true)
    );
  }

  // Makes sure there is a clean, unprocessed <video> element
  // to hand to Video.js.
  ensureVideoElement() {
    const key = this.cacheKey();

    const nativeVideo = Array.from(
      this.element.querySelectorAll(
        "video[data-video-js-target='video']"
      )
    ).find(video => this.isPristine(video));

    if (nativeVideo) {
      pristineVideoCache.set(
        key,
        nativeVideo.cloneNode(true)
      );
    }

    const storedClone = pristineVideoCache.get(key);

    if (!storedClone) return null;

    const freshVideo = storedClone.cloneNode(true);

    this.element.replaceChildren(freshVideo);

    return freshVideo;
  }

  isPristine(element) {
    return !element.classList.contains("vjs-tech");
  }

  cacheKey() {
    return this.element.id || this.element;
  }

  // ----------------------------------------------------
  // User Pause / Play Tracking
  // ----------------------------------------------------

  /**
   * Track clicks on Video.js's native play/pause button.
   *
   * This runs in capture phase so we can determine whether the
   * user is trying to pause BEFORE Video.js performs its own
   * play/pause operation.
   */
  setupNativePlayToggleTracking() {
    if (!this.player || this.nativePlayToggleHandler) return;

    const playToggle = this.player.controlBar?.playToggle;

    if (!playToggle) return;

    this.nativePlayToggleElement =
      playToggle.el();

    if (!this.nativePlayToggleElement) return;

    this.nativePlayToggleHandler = () => {
      if (!this.player || this.player.isDisposed()) return;

      // If currently playing, the user is about to pause.
      if (this.player.paused()) {
        // User is manually pressing Play.
        this.userPaused = false;
        this.element.dataset.userPaused = "false";
      } else {
        this.manualPauseRequested = true;
      }
    };

    this.nativePlayToggleElement.addEventListener(
      "click",
      this.nativePlayToggleHandler,
      true
    );
  }

  removeNativePlayToggleTracking() {
    if (
      !this.nativePlayToggleElement ||
      !this.nativePlayToggleHandler
    ) {
      return;
    }

    this.nativePlayToggleElement.removeEventListener(
      "click",
      this.nativePlayToggleHandler,
      true
    );

    this.nativePlayToggleElement = null;
    this.nativePlayToggleHandler = null;
  }

  /**
   * Clear the manual pause state.
   *
   * This is used when the user explicitly starts the video.
   */
  markUserPlaying() {
    this.userPaused = false;
    this.element.dataset.userPaused = "false";
    this.manualPauseRequested = false;
  }

  /**
   * Mark the video as manually paused.
   *
   * This is used by the custom center play/pause button.
   */
  markUserPaused() {
    this.userPaused = true;
    this.element.dataset.userPaused = "true";
    this.manualPauseRequested = false;
  }

  // ----------------------------------------------------
  // Global Mute
  // ----------------------------------------------------

  volumeChanged = () => {
    const muted = this.player.muted();

    if (this.temporaryAutoplayMuted) {
      if (muted) return;

      this.temporaryAutoplayMuted = false;
    }

    if (globalThis.feedMuted === muted) return;

    globalThis.feedMuted = muted;

    try {
      globalThis.localStorage.setItem(
        FEED_MUTED_STORAGE_KEY,
        muted.toString()
      );
    } catch {
      // Storage may be unavailable in private browsing contexts.
    }

    document.dispatchEvent(
      new CustomEvent("feed:mute-change", {
        detail: { muted }
      })
    );
  };

  muteListener = event => {
    if (!this.player || this.player.isDisposed()) return;

    this.player.muted(event.detail.muted);
  };

  handlePlaybackError = () => {
    const error = this.player?.error();
    console.log(error, "===");


    // MEDIA_ERR_NETWORK: the download started, then stopped unexpectedly.
    // Decode and unsupported-source failures need a new upload/encoding and
    // must not be retried in a loop.
    if (error?.code !== 2 || this.networkRetryCount >= 2) return;

    const retryDelay = this.networkRetryCount === 0 ? 750 : 2000;
    const resumeAt = this.player.currentTime() || 0;
    const shouldResume = !this.userPaused;

    this.networkRetryCount += 1;
    clearTimeout(this.networkRetryTimeout);

    this.networkRetryTimeout = setTimeout(() => {
      if (!this.player || this.player.isDisposed()) return;

      const retryUrl = new URL(this.sourceUrl, globalThis.location.href);
      retryUrl.searchParams.set("media_retry", Date.now().toString());

      this.player.error(null);
      this.player.src({ src: retryUrl.toString() });

      this.player.one("loadedmetadata", () => {
        if (resumeAt > 0 && Number.isFinite(this.player.duration())) {
          this.player.currentTime(
            Math.min(resumeAt, Math.max(0, this.player.duration() - 0.1))
          );
        }

        if (shouldResume) this.play();
      });

      this.player.load();
    }, retryDelay);
  };

  // ----------------------------------------------------
  // Playback
  // ----------------------------------------------------

  isPlaying() {
    return (
      !!this.player &&
      !this.player.isDisposed() &&
      !this.player.paused()
    );
  }

  // Runs `task` only after any previously queued play()/pause()
  // call for this player has finished.
  enqueue(task) {
    this.operationQueue = this.operationQueue
      .then(task)
      .catch(() => { });

    return this.operationQueue;
  }

  /**
   * Play the video.
   *
   * By default this is considered an automatic/system play.
   *
   * userInitiated: true is used when the actual user presses
   * the play button.
   */
  play({ userInitiated = false } = {}) {
    if (userInitiated) {
      this.markUserPlaying();
    }

    return this.enqueue(async () => {
      if (
        !this.player ||
        this.player.isDisposed()
      ) {
        return;
      }

      if (this.isPlaying()) return;

      try {
        this.temporaryAutoplayMuted = false;
        this.player.muted(globalThis.feedMuted);

        await this.player.play();
      } catch {
        // Browsers may block unmuted autoplay without a user gesture.
        // Fall back to muted playback without overwriting the user's
        // saved audio preference. A subsequent user gesture can unmute.
        if (!this.player.muted()) {
          try {
            this.temporaryAutoplayMuted = true;
            this.player.muted(true);

            await this.player.play();
          } catch {
            this.temporaryAutoplayMuted = false;
          }
        }
      }
    });
  }

  /**
   * Pause the video.
   *
   * IMPORTANT:
   *
   * userInitiated: false
   *   -> automatic pause, such as scrolling out of viewport.
   *
   * userInitiated: true
   *   -> user explicitly clicked pause.
   */
  pause({ userInitiated = false } = {}) {
    if (userInitiated) {
      this.markUserPaused();
    }

    return this.enqueue(async () => {
      if (
        !this.player ||
        this.player.isDisposed()
      ) {
        return;
      }

      if (this.player.paused()) return;

      this.player.pause();
    });
  }

  // ----------------------------------------------------
  // Fullscreen
  // ----------------------------------------------------

  setupFullscreenPortal() {
    const playerElement = this.player.el();

    this.fullscreenPortalState = {
      playerElement,
      originalParent: playerElement.parentNode,
      originalNextSibling: playerElement.nextSibling,
      originalRequestFullscreen: this.player.requestFullscreen.bind(this.player),
      fullscreenApi: this.player.fsApi_,
      feedController: this.element
        .closest("[data-controller~='feed-video']")
        ?.feedVideoController,
      placeholder: null,
      portal: null,
      resumePlaybackOnFullscreen: false,
      fullscreenResumeTimers: [],
      fullscreenRequestPending: false
    };

    this.bindFullscreenPortalEvents();
  }

  bindFullscreenPortalEvents() {
    this.fullscreenPortalHandlers = {
      playerFullscreenChange:
        this.syncPlayerFullscreenPlayback.bind(this),

      documentFullscreenChange:
        this.syncDocumentFullscreen.bind(this),

      dispose:
        this.destroyFullscreenPortal.bind(this)
    };

    this.player.on(
      "fullscreenchange",
      this.fullscreenPortalHandlers.playerFullscreenChange
    );

    const { fullscreenApi } = this.fullscreenPortalState;

    if (fullscreenApi?.fullscreenchange) {
      document.addEventListener(
        fullscreenApi.fullscreenchange,
        this.fullscreenPortalHandlers.documentFullscreenChange
      );
    }

    this.player.requestFullscreen =
      this.requestFullscreenPortal.bind(this);

    this.player.on(
      "dispose",
      this.fullscreenPortalHandlers.dispose
    );
  }

  clearFullscreenResumeTimers() {
    const { fullscreenResumeTimers } = this.fullscreenPortalState;

    for (const timer of fullscreenResumeTimers) {
      clearTimeout(timer);
    }

    this.fullscreenPortalState.fullscreenResumeTimers = [];
  }

  scheduleFullscreenPlaybackResume() {
    this.clearFullscreenResumeTimers();

    const delays = [0, 100, 300, 700];

    this.fullscreenPortalState.fullscreenResumeTimers = delays.map(
      (delay) => setTimeout(() => {
        this.resumeFullscreenPlayback(delay);
      }, delay)
    );
  }

  resumeFullscreenPlayback(delay) {
    const shouldResume =
      this.fullscreenPortalState.resumePlaybackOnFullscreen &&
      !this.userPaused &&
      this.player &&
      !this.player.isDisposed() &&
      this.player.isFullscreen();

    if (!shouldResume) return;

    if (this.player.paused()) {
      this.player.play().catch(() => { });
    }

    if (delay === 700) {
      this.fullscreenPortalState.resumePlaybackOnFullscreen = false;
      this.clearFullscreenResumeTimers();
    }
  }

  restoreFullscreenPlayer() {
    const state = this.fullscreenPortalState;

    if (!state) return;

    state.resumePlaybackOnFullscreen = false;
    this.clearFullscreenResumeTimers();

    state.playerElement.classList.remove("vjs-portal-active");

    this.restorePlayerPosition();

    state.placeholder?.remove();
    state.placeholder = null;

    state.portal?.remove();
    state.portal = null;

    state.feedController?.resumeSliderResponsiveUpdates();
  }

  restorePlayerPosition() {
    const {
      playerElement,
      originalParent,
      originalNextSibling
    } = this.fullscreenPortalState;

    if (playerElement.parentNode === originalParent) return;

    if (originalNextSibling?.parentNode === originalParent) {
      originalNextSibling.before(playerElement);
    } else {
      originalParent.appendChild(playerElement);
    }
  }

  syncDocumentFullscreen() {
    const {
      playerElement,
      portal,
      fullscreenApi
    } = this.fullscreenPortalState;

    if (!playerElement.classList.contains("vjs-portal-active")) {
      return;
    }

    const fullscreenElement = document[fullscreenApi.fullscreenElement];

    const isFullscreen =
      fullscreenElement === portal ||
      fullscreenElement === playerElement;

    this.fullscreenPortalState.fullscreenRequestPending = false;

    this.player.isFullscreen(isFullscreen);

    this.player.controlBar?.fullscreenToggle?.handleFullscreenChange();

    if (isFullscreen) {
      this.scheduleFullscreenPlaybackResume();
    } else {
      this.restoreFullscreenPlayer();
    }
  }

  syncPlayerFullscreenPlayback() {
    if (this.player.isFullscreen()) {
      this.scheduleFullscreenPlaybackResume();
    }
  }

  createFullscreenPortal() {
    const state = this.fullscreenPortalState;
    const { playerElement, feedController } = state;

    if (playerElement.parentNode !== state.originalParent) {
      return;
    }

    // Entering browser fullscreen changes window.innerWidth.
    // Freeze Slick's breakpoint refresh first.
    feedController?.suspendSliderResponsiveUpdates();

    const bounds = playerElement.getBoundingClientRect();

    state.placeholder = document.createElement("div");
    state.placeholder.className = "vjs-fullscreen-placeholder";
    state.placeholder.style.height = `${bounds.height}px`;
    state.placeholder.setAttribute("aria-hidden", "true");

    playerElement.before(state.placeholder);

    state.portal = document.createElement("div");
    state.portal.className = "vjs-fullscreen-portal";

    document.body.appendChild(state.portal);

    playerElement.classList.add("vjs-portal-active");
    state.portal.appendChild(playerElement);
  }

  requestFullscreenPortal(fullscreenOptions) {
    const state = this.fullscreenPortalState;

    if (
      state.fullscreenRequestPending ||
      this.player.isFullscreen()
    ) {
      return;
    }

    state.fullscreenRequestPending = true;

    state.resumePlaybackOnFullscreen =
      !this.player.paused() && !this.player.ended();

    this.createFullscreenPortal();

    try {
      const result = this.executeFullscreenRequest(fullscreenOptions);

      if (result?.then) {
        return result
          .then(() => {
            state.fullscreenRequestPending = false;
            this.scheduleFullscreenPlaybackResume();
          })
          .catch(() => {
            state.fullscreenRequestPending = false;
            this.restoreFullscreenPlayer();
          });
      }

      state.fullscreenRequestPending = false;

      return result;
    } catch (error) {
      state.fullscreenRequestPending = false;
      this.restoreFullscreenPlayer();

      throw error;
    }
  }

  executeFullscreenRequest(fullscreenOptions) {
    const {
      fullscreenApi,
      portal,
      originalRequestFullscreen
    } = this.fullscreenPortalState;

    const documentRequestFullscreen =
      fullscreenApi?.requestFullscreen &&
      portal?.[fullscreenApi.requestFullscreen];

    const requestOptions = fullscreenApi.prefixed
      ? undefined
      : fullscreenOptions;

    if (documentRequestFullscreen) {
      return documentRequestFullscreen.call(
        portal,
        requestOptions
      );
    }

    return originalRequestFullscreen(fullscreenOptions);
  }

  destroyFullscreenPortal() {
    const { fullscreenApi } = this.fullscreenPortalState;
    const handlers = this.fullscreenPortalHandlers;

    this.player.off(
      "fullscreenchange",
      handlers.playerFullscreenChange
    );

    this.player.off("dispose", handlers.dispose);

    if (fullscreenApi?.fullscreenchange) {
      document.removeEventListener(
        fullscreenApi.fullscreenchange,
        handlers.documentFullscreenChange
      );
    }

    this.restoreFullscreenPlayer();
  }

  // ----------------------------------------------------
  // Custom Center Play Button
  // ----------------------------------------------------

  addCenterPlayButton() {
    const wrapper = this.element.parentNode;

    if (!wrapper) return;

    // Guard against duplicate button.
    wrapper
      .querySelector(":scope > .center-play-btn")
      ?.remove();

    wrapper.classList.add(
      "custom-video-wrapper",
      "position-relative"
    );

    this.element.classList.add(
      "custom-controls-enabled"
    );

    const btn = document.createElement("div");

    btn.classList.add(
      "center-play-btn",
      "size-70",
      "position-absolute",
      "top-50",
      "start-50",
      "translate-middle",
      "d-flex",
      "align-items-center",
      "justify-content-center",
      "rounded-circle",
      "cursor-pointer"
    );

    btn.innerHTML = this.playIcon();

    btn.addEventListener("click", () => {
      if (this.player.paused()) {
        // User explicitly pressed Play.
        this.play({
          userInitiated: true
        });
      } else {
        // User explicitly pressed Pause.
        this.pause({
          userInitiated: true
        });
      }
    });

    wrapper.appendChild(btn);

    this.player.on("play", () => {
      btn.innerHTML = this.pauseIcon();
    });

    this.player.on("pause", () => {
      btn.innerHTML = this.playIcon();
    });
  }

  playIcon() {
    return `
      <svg
        class="pe-none"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="white"
      >
        <polygon points="8,5 19,12 8,19"></polygon>
      </svg>
    `;
  }

  pauseIcon() {
    return `
      <svg
        class="pe-none"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="white"
      >
        <rect x="6" y="5" width="4" height="14"></rect>
        <rect x="14" y="5" width="4" height="14"></rect>
      </svg>
    `;
  }
}

VideoJsController.currentlyPlayingController = null;
